// src/components/main/gallery.js
import React, {
  useEffect,
  useMemo,
  useRef,
  useState,
  useLayoutEffect,
} from "react";
import { createPortal } from "react-dom";
import { Link, useLocation } from "react-router-dom";
import "./gallery.css";
import AudioFloat from "./AudioFloat.js";

/* ---------------- tiny utils ---------------- */
const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

const prettify = (s = "") =>
  s
    .replace(/^\/+|\/+$/g, "")
    .split("-")
    .map((w) => (w ? w[0].toUpperCase() + w.slice(1) : ""))
    .join(" ");

const pathParts = (pathname = "/") => {
  const [continent = "", country = ""] = pathname
    .replace(/^\/+|\/+$/g, "")
    .split("/");
  return { continent, country };
};

const orientationOf = (it = {}) =>
  String(it?.orientation || "").trim().toLowerCase();

const isPortrait = (it = {}) => {
  const o = orientationOf(it);
  if (["portrait", "vertical", "tall"].includes(o)) return true;
  if (["landscape", "horizontal", "wide"].includes(o)) return false;
  return null;
};

const isVideoUrl = (url = "") =>
  /(\.mp4(\?|$)|\.mov(\?|$)|\.webm(\?|$)|\/video\/|mime=video)/i.test(url);

const kindOfItem = (item = {}) => {
  const t = String(item.type || "").toLowerCase();
  if (t === "video") return "video";
  if (t === "image") return "image";
  if (t === "phrase") return "phrase";
  return isVideoUrl(item.url) ? "video" : "image";
};

const pickByOrientation = (arr = [], wantPortrait) => {
  if (!arr.length) return null;
  const known = arr.filter((it) => isPortrait(it) !== null);
  const preferred = known.filter((it) => isPortrait(it) === !!wantPortrait);
  const pool = preferred.length ? preferred : known.length ? known : arr;
  return pool[Math.floor(Math.random() * pool.length)];
};

function shuffle(arr = []) {
  for (let i = arr.length - 1; i > 0; i--) {
    const j = (Math.random() * (i + 1)) | 0;
    [arr[i], arr[j]] = [arr[j]];
  }
  return arr;
}

/* Stable sort-by-order helper (items lacking `order` keep original order, after ordered ones) */
const sortByOrderStable = (arr = []) =>
  arr
    .map((it, i) => ({ _i: i, it }))
    .sort((a, b) => {
      const ao = Number(a.it?.order);
      const bo = Number(b.it?.order);
      const aHas = Number.isFinite(ao);
      const bHas = Number.isFinite(bo);
      if (aHas && bHas) return ao - bo;
      if (aHas) return -1;
      if (bHas) return 1;
      return a._i - b._i;
    })
    .map(({ it }) => it);

/* ---------------- phrase library ---------------- */
const tease = (continent, country) => {
  const key = `${continent}/${country}`.toLowerCase();
  const lib = {
    "africa/madagascar": [
      "Red earth, ancient baobabs, and seas the color of dreamlight.",
      "Here, silence carries stories—the rest is written in wind and salt.",
    ],
    "africa/madagascar-wildlife": [
      "Lemurs in the canopy, chameleons in slow poetry.",
      "The island’s heartbeat lives between shadow and sun.",
    ],
    "africa/south-africa-wildlife": [
      "Lions in the golden hush, elephants in the dust’s slow dance.",
      "The wild breathes here—vast, ancient, unafraid.",
    ],
    "asia/india": [
      "Heat shimmer and temple bells over a river made of time.",
      "Gold dust on the air, and a thousand colors at rest.",
    ],
    "asia/bhutan": [
      "Clouds comb the mountains; prayer flags translate the sky.",
      "Quiet is an art form here—and the valley listens.",
    ],
    "southamerica/brazil-amazon": [
      "Green to the horizon; the river writes in silver.",
      "Every breath is rainforest—ancient, patient, alive.",
    ],
    "africa/lesotho": [
      "Shepherds move through mist with songs older than the hills.",
      "Wool, wind, and warmth — the mountain people carry the sky with them.",
    ],
    "asia/nepal": [
      "Prayer flags flutter where the air becomes sky.",
      "The Himalayas whisper lessons only stillness can hear.",
    ],
    "asia/myanmar": [
      "Golden spires rise from morning mist and quiet devotion.",
      "Time moves slowly here, like a boat across sacred water.",
    ],
    "asia/laos": [
      "The Mekong glows under saffron skies and temple bells.",
      "Peace drifts through palms and laughter like incense in air.",
    ],
    "southamerica/brazil-buzios": [
      "Waves curl around laughter and sun-touched skin.",
      "Evening falls in gold — music, sea, and memory intertwined.",
    ],
    "southamerica/argentina-ushuaia": [
      "Where the world ends, freedom begins.   Ushuaia - a place where wind, soul, and silence ride together.",
    ],
    "australia/sydney-bondibeach": [
      "In Bondi Beach, waves rise like thunder against the sculpted cliffs.",
      "Here, the sea doesn’t rest — it roars, endlessly alive.",
    ],
    "australia/sydney": [
      "Waves carve the sandstone edges of Sydney like memory itself.",
      "Between thunder and light, the ocean keeps the city awake.",
    ],
    "antarctica/": ["Journey through the white continent"],
  };
  const candidates = lib[key] || [
    `${prettify(country || continent)}—where light takes the long way through.`,
    "Let the pace fall away; the details are the story.",
  ];
  return candidates[Math.floor(Math.random() * candidates.length)];
};

/* ---------------- image/video average color ---------------- */
function averageFromImageSrc(src) {
  return new Promise((resolve) => {
    if (!src) return resolve(null);
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.src = src;
    img.onload = () => {
      try {
        const w = 16,
          h = 16;
        const c = document.createElement("canvas");
        c.width = w;
        c.height = h;
        const ctx = c.getContext("2d", { willReadFrequently: true });
        ctx.drawImage(img, 0, 0, w, h);
        const { data } = ctx.getImageData(0, 0, w, h);
        let r = 0,
          g = 0,
          b = 0,
          n = 0;
        for (let i = 0; i < data.length; i += 4) {
          r += data[i];
          g += data[i + 1];
          b += data[i + 2];
          n++;
        }
        resolve({
          r: Math.round(r / (n || 1)),
          g: Math.round(g / (n || 1)),
          b: Math.round(b / (n || 1)),
        });
      } catch {
        resolve(null);
      }
    };
    img.onerror = () => resolve(null);
  });
}

function averageFromVideoEl(video) {
  try {
    if (!video || video.readyState < 2) return null;
    const w = 16,
      h = 16;
    const c = document.createElement("canvas");
    c.width = w;
    c.height = h;
    const ctx = c.getContext("2d", { willReadFrequently: true });
    ctx.drawImage(video, 0, 0, w, h);
    const { data } = ctx.getImageData(0, 0, w, h);
    let r = 0,
      g = 0,
      b = 0,
      n = 0;
    for (let i = 0; i < data.length; i += 4) {
      r += data[i];
      g += data[i + 1];
      b += data[i + 2];
      n++;
    }
    return {
      r: Math.round(r / (n || 1)),
      g: Math.round(g / (n || 1)),
      b: Math.round(b / (n || 1)),
    };
  } catch {
    return null;
  }
}

/* ---------------- layout helpers ---------------- */
function buildEvenMix(items = [], normType) {
  const buckets = { image: [], video: [], phrase: [] };
  items.forEach((it) => {
    const t = normType(it);
    if (t === "image" || t === "video" || t === "phrase") {
      buckets[t].push(it);
    } else {
      buckets.image.push(it);
    }
  });

  for (const k of Object.keys(buckets)) shuffle(buckets[k]);

  const remain = {
    image: buckets.image.length,
    video: buckets.video.length,
    phrase: buckets.phrase.length,
  };

  const out = [];
  let last = null;

  while (remain.image + remain.video + remain.phrase > 0) {
    let candidates = Object.entries(remain).filter(
      ([t, c]) => c > 0 && t !== last
    );
    if (!candidates.length)
      candidates = Object.entries(remain).filter(([_, c]) => c > 0);
    candidates.sort((a, b) => b[1] - a[1]);
    const pickType = candidates[0][0];

    const arr = buckets[pickType];
    out.push(arr.shift());
    remain[pickType]--;
    last = pickType;
  }
  return out;
}

/* ---------------- skeletons & tiles ---------------- */
function Skeleton({ phAR = "3 / 2" }) {
  return (
    <div className="skeleton" style={{ aspectRatio: phAR }}>
      <div className="skeleton-shimmer" />
    </div>
  );
}

function VideoSkeleton({ phAR = "16 / 9", poster }) {
  return (
    <div
      className="video-skeleton"
      style={{ aspectRatio: phAR }}
      aria-hidden="true"
    >
      {poster ? (
        <img
          className="video-skeleton-poster"
          src={poster}
          alt=""
          decoding="async"
          loading="eager"
        />
      ) : null}
      <div className="skeleton-shimmer" />
    </div>
  );
}

function ImageTile({
  id,
  index,
  url,
  half,
  isActive,
  onBecameActive,
  framedOk,
  phAR = "3 / 2",
  onOpenLightbox,
}) {
  const ref = useRef(null);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!ref.current) return;
    ref.current.dataset.id = id;
    ref.current.dataset.index = String(index);
    ref.current.dataset.half = half ? "1" : "0";
    ref.current.classList.toggle("half", !!half);
    ref.current.classList.toggle("full", !half);
  }, [id, index, half]);

  useEffect(() => {
    if (isActive && loaded) onBecameActive?.({ src: url, index, kind: "image" });
  }, [isActive, loaded, url, index, onBecameActive]);

  return (
    <div
      ref={ref}
      className={`media-item image ${half ? "half" : "full"} ${
        isActive ? "active" : ""
      } ${framedOk ? "framed-ok" : ""} ${loaded ? "is-visible" : "is-loading"}`}
      data-id={id}
      style={{ transitionDelay: `${(index % 8) * 40}ms` }}
      onClick={() => onOpenLightbox?.(index)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpenLightbox?.(index);
      }}
    >
      {!loaded && <Skeleton phAR={phAR} />}
      <img
        className="media-el"
        src={url}
        alt=""
        loading="lazy"
        decoding="async"
        onLoad={() => setLoaded(true)}
      />
    </div>
  );
}

function PhraseTile({ id, index, text, onOpenLightbox }) {
  return (
    <div
      className="media-item phrase full is-visible"
      data-id={id}
      data-index={index}
      style={{ transitionDelay: `${(index % 8) * 40}ms` }}
      onClick={() => onOpenLightbox?.(index)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter") onOpenLightbox?.(index);
      }}
    >
      <blockquote className="phrase-text">{text}</blockquote>
    </div>
  );
}

/* ---------------- video chip helpers ---------------- */
const isPortraitHint = (v) => {
  const o = String(v.orientation || "").toLowerCase();
  if (["portrait", "vertical", "tall"].includes(o)) return true;
  if (["landscape", "horizontal", "wide"].includes(o)) return false;
  const a = String(v.aspect || "");
  if (/^9\s*[:/]\s*16$/i.test(a)) return true;
  if (/^16\s*[:/]\s*9$/i.test(a)) return false;
  return null;
};

/* ---------------- music mapping (NEW) ---------------- */
const MUSIC_BASE = "https://lvltravel.s3.us-east-2.amazonaws.com/Music/";

function resolveTrackFilename(continentRaw, countryRaw) {
  const continent = String(continentRaw || "").toLowerCase();
  const country = String(countryRaw || "").toLowerCase();

  // Priority: country segment (e.g., /africa/lesotho) → then continent (e.g., /antarctica)
  const slug = country || continent;

  // Map of route slug → filename
  const map = {
    "antarctica": "antarctica.mp3",
    "south-africa-wildlife": "africawildlife.mp3",
    "brazil-amazon": "amazon.mp3",
    "india": "india.mp3",          // case-sensitive per your note
    "lesotho": "lesotho.mp3",
    "madagascar": "madagascar.mp3",
    "nepal": "nepal.mp3",
    "sydney-bondibeach": "australiabeach.mp3",
     "bhutan": "bhutan.mp3",
  };

  return map[slug] || "2ndChakra.mp3"; // default/fallback
}

function resolveStartTime(continentRaw, countryRaw) {
  const continent = String(continentRaw || "").toLowerCase();
  const country = String(countryRaw || "").toLowerCase();
  const slug = country || continent;

  if (slug === "south-africa-wildlife") return 220; // "3 minutes"? (note: 3 min = 180)
  else if (slug === "madagascar") return 5;
  return 0;

  
}

/* ---------------- main ---------------- */
export default function Gallery({
  items = [],
  loading,
  error,
  fetchForCurrentPath,
}) {
  const location = useLocation();
  const gridRef = useRef(null);

  /* Reset scroll on route change */
  useLayoutEffect(() => {
    if ("scrollRestoration" in window.history)
      window.history.scrollRestoration = "manual";
    window.scrollTo(0, 0);
  }, [location.pathname]);

  /* ===== Scroll cue state/effect ===== */
  const [showScrollCue, setShowScrollCue] = useState(true);
  useEffect(() => {
    const onScroll = () => setShowScrollCue((window.scrollY || 0) < 40);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, [location.pathname]);

  useEffect(() => {
    fetchForCurrentPath?.();
  }, [location.pathname, fetchForCurrentPath]);

  /* Titles */
  const { continent, country } = useMemo(
    () => pathParts(location.pathname),
    [location.pathname]
  );
  const titleContinent = prettify(continent || "");
  const titleCountry = country ? prettify(country) : "";

  const continentTeaser = useMemo(
    () => tease(continent, country),
    [continent, country]
  );

  /* ---------- Dynamic music src (NEW) ---------- */
  const musicSrc = useMemo(() => {
    const filename = resolveTrackFilename(continent, country);
    return `${MUSIC_BASE}${filename}`;
  }, [continent, country]);

  const musicStart = useMemo(() => resolveStartTime(continent, country), [continent, country]);

  /* Basic lists */
  const baseList = useMemo(
    () => (Array.isArray(items) ? items.slice() : []),
    [items]
  );

  const normType = (it) => String(it?.type ?? "image").trim().toLowerCase();

  /* ---------- ORDERED lists ---------- */
  const videosOrdered = useMemo(
    () => sortByOrderStable(baseList.filter((it) => normType(it) === "video")),
    [baseList]
  );

  const gridList = useMemo(() => {
    const gp = baseList
      .filter((it) => {
        const t = normType(it);
        return t === "image" || t === "phrase";
      })
      .map((it) => {
        if (normType(it) !== "phrase") return it;
        const text =
          it.text ||
          it.content ||
          it.title ||
          it.copy ||
          it.caption ||
          it.description ||
          it.body ||
          it.phrase ||
          "";
        return { ...it, __phraseText: text };
      });
    return sortByOrderStable(gp);
  }, [baseList]);

  const mediaOnlyOrdered = useMemo(
    () => gridList.filter((it) => normType(it) !== "phrase"),
    [gridList]
  );
  const imagesOrdered = useMemo(
    () => gridList.filter((it) => normType(it) === "image"),
    [gridList]
  );

  /* ----- entry media (prefer items with entry === true; else random LANDSCAPE) ----- */
  const [entryMedia, setEntryMedia] = useState(null);
  const [entryLoaded, setEntryLoaded] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setEntryMedia(null);
    setEntryLoaded(false);
    if (!imagesOrdered.length && !baseList.length) return;

    const looksLandscape = (it) => {
      const o = orientationOf(it);
      const aspect = String(it?.aspect || "");
      return (
        o === "landscape" ||
        o === "horizontal" ||
        o === "wide" ||
        /^16\s*[:/]\s*9$/i.test(aspect) ||
        /^3\s*[:/]\s*2$/i.test(aspect) ||
        /^4\s*[:/]\s*3$/i.test(aspect)
      );
    };

    // 1) Prefer items explicitly tagged entry === true
    const entryTagged = imagesOrdered.filter((it) => it?.entry === true);
    const entryTaggedLandscape = entryTagged.filter(looksLandscape);

    // 2) Fallbacks when no tagged entries exist
    const landscapeImages = imagesOrdered.filter(looksLandscape);

    // Build the pool in priority order:
    const pool =
      entryTaggedLandscape.length
        ? entryTaggedLandscape
        : entryTagged.length
        ? entryTagged
        : landscapeImages.length
        ? landscapeImages
        : imagesOrdered.length
        ? imagesOrdered
        : baseList;

    if (!pool.length) return;
    const pick = pool[(Math.random() * pool.length) | 0];
    if (cancelled || !pick?.url) return;

    setEntryMedia({ kind: "image", src: pick.url, poster: pick.poster || null });

    return () => {
      cancelled = true;
    };
  }, [location.pathname, imagesOrdered, baseList]);

  /* ----- cinematic blurred page background ----- */
  const [bgA, setBgA] = useState(null);
  const [bgB, setBgB] = useState(null);
  const [useAOnTop, setUseAOnTop] = useState(true);
  const [bgTint, setBgTint] = useState(
    "linear-gradient(180deg, rgba(0,0,0,0.14), rgba(0,0,0,0.38))"
  );
  const [heroImgOpacity, setHeroImgOpacity] = useState(0.7);
  const [heroTintOpacity, setHeroTintOpacity] = useState(0.3);
  const [bgReady, setBgReady] = useState(false);

  useEffect(() => {
    setBgA(null);
    setBgB(null);
    setUseAOnTop(true);
    setBgReady(false);
  }, [location.pathname]);

  useEffect(() => {
    let cancelled = false;
    const poolBase = imagesOrdered.length ? imagesOrdered : mediaOnlyOrdered;
    if (!poolBase.length) return;
    const isMobile = window.matchMedia("(max-width: 767px)").matches;
    const wantPortrait = isMobile;
    const pick = pickByOrientation(poolBase, wantPortrait);
    if (cancelled || !pick) return;
    const src = pick.url || null;
    if (!src) return;
    setBgA(src);
    setUseAOnTop(true);
    return () => {
      cancelled = true;
    };
  }, [imagesOrdered.length, mediaOnlyOrdered.length]);

  useEffect(() => {
    let cancelled = false;
    const src = useAOnTop ? bgA : bgB;
    if (src) {
      (async () => {
        const isVid = /\.(mp4|mov|webm|avi)/i.test(src);
        let avg = null;
        if (isVid) {
          avg = { r: 20, g: 20, b: 20 };
        } else {
          avg = await averageFromImageSrc(src);
        }
        if (cancelled || !avg) return;
        const { r, g, b } = avg;
        setBgTint(
          `linear-gradient(180deg, rgba(${r},${g},${b},0.18), rgba(0,0,0,0.40))`
        );
        setBgReady(true);
      })();
    }
    return () => {
      cancelled = true;
    };
  }, [bgA, bgB, useAOnTop]);

  const rafRef = useRef(0);
  useEffect(() => {
    const BASE_IMG = 0.62,
      MIN_IMG = 0.56;
    const BASE_TINT = 0.36,
      MIN_TINT = 0.24;
    const DIST = 900;

    const PARALLAX_STRENGTH = 0.06;
    const PARALLAX_MAX = 42;

    const onScroll = () => {
      if (rafRef.current) return;
      rafRef.current = requestAnimationFrame(() => {
        const y = window.scrollY || 0;

        const p = Math.max(0, Math.min(1, 1 - y / DIST));
        setHeroImgOpacity(MIN_IMG + (BASE_IMG - MIN_IMG) * p);
        setHeroTintOpacity(MIN_TINT + (BASE_TINT - MIN_TINT) * p);

        const offset = clamp(y * PARALLAX_STRENGTH, -PARALLAX_MAX, PARALLAX_MAX);
        document.documentElement.style.setProperty(
          "--hero-parallax",
          `${offset}px`
        );

        rafRef.current = 0;
      });
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = 0;
    };
  }, []);

  /* ---------- pagination ---------- */
  const PAGE_SIZE = 30;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  useEffect(() => {
    setVisibleCount(PAGE_SIZE);
  }, [location.pathname, gridList.length]);

  const showing = gridList.slice(0, visibleCount);
  const remaining = Math.max(0, gridList.length - visibleCount);

  const loadMore = () =>
    setVisibleCount((c) => Math.min(c + PAGE_SIZE, Math.max(0, gridList.length)));

  const handleLoadMoreClick = () => {
    setAutoLoad(true);
    loadMore();
  };

  const sentinelRef = useRef(null);
  const [autoLoad, setAutoLoad] = useState(false);

  useEffect(() => {
    const onScroll = () => {
      const y = window.scrollY || 0;
      const h = window.innerHeight || 1;
      const doc = Math.max(
        document.body.scrollHeight,
        document.documentElement.scrollHeight
      );
      const at70 = (y + h) / doc > 0.7;
      if (at70) setAutoLoad(true);
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    if (!autoLoad) return;
    const el = sentinelRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) loadMore();
        });
      },
      { root: null, rootMargin: "200px 0px 200px 0px", threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [gridList.length, autoLoad]);

  /* ----- hero background tracking from visible tiles ----- */
  const [activeIds, setActiveIds] = useState([]);
  const ratioMapRef = useRef(new Map());
  const rafPickRef = useRef(0);
  const lastSwitchRef = useRef(0);
  const MIN_BG_DWELL_MS = 900;

  const updateHeroToSrc = (src) => {
    if (!src) return;
    const now = performance.now();
    if (now - lastSwitchRef.current < MIN_BG_DWELL_MS) return;
    const currentTopSrc = useAOnTop ? bgA : bgB;
    if (currentTopSrc === src) return;
    if (useAOnTop) setBgB(src);
    else setBgA(src);
    requestAnimationFrame(() => {
      setUseAOnTop((v) => !v);
      lastSwitchRef.current = performance.now();
    });
  };

  const isIdActive = (id) => activeIds.includes(id);
  const handleBecameActive = ({ src, kind }) => {
    if (kind === "image" && src) updateHeroToSrc(src);
  };

  useEffect(() => {
    const root = gridRef.current;
    if (!root) return;
    const itemsEls = Array.from(root.querySelectorAll(".media-item"));
    itemsEls.forEach((el, i) => {
      if (!el.dataset.id) el.dataset.id = String(i);
      el.dataset.half = el.classList.contains("half") ? "1" : "0";
    });

    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          const el = entry.target;
          const id = el.dataset.id;
          if (!id) return;

          if (entry.isIntersecting) {
            const rect = el.getBoundingClientRect();
            const isImageEl = el.classList.contains("image");
            const isVideoEl = el.classList.contains("video");
            const imgEl = isImageEl ? el.querySelector("img.media-el") : null;
            const videoEl = isVideoEl ? el.querySelector("video.video-el") : null;
            const src = imgEl?.currentSrc || imgEl?.src || null;
            ratioMapRef.current.set(id, {
              ratio: entry.intersectionRatio,
              top: Math.round(rect.top),
              half: el.dataset.half === "1",
              kind: isImageEl ? "image" : isVideoEl ? "video" : "other",
              src,
              videoEl,
            });
          } else {
            ratioMapRef.current.delete(id);
          }
        });

        if (!rafPickRef.current) {
          rafPickRef.current = requestAnimationFrame(() => {
            rafPickRef.current = 0;

            let bestId = null;
            let best = {
              ratio: 0,
              top: 0,
              half: false,
              kind: "other",
              src: null,
              videoEl: null,
            };
            for (const [id, info] of ratioMapRef.current.entries()) {
              if (info.ratio >= best.ratio) {
                bestId = id;
                best = info;
              }
            }
            if (!bestId) setActiveIds([]);
            else setActiveIds([bestId]);

            if (best.kind === "image" && best.src) {
              updateHeroToSrc(best.src);
              averageFromImageSrc(best.src).then((avg) => {
                if (!avg) return;
                const { r, g, b } = avg;
                setBgTint(
                  `linear-gradient(180deg, rgba(${r},${g},${b},0.22), rgba(0,0,0,0.44))`
                );
              });
            } else if (best.kind === "video" && best.videoEl) {
              const bgSrc = best.videoEl.currentSrc || best.videoEl.src || null;
              if (bgSrc) updateHeroToSrc(bgSrc);
              const avg = averageFromVideoEl(best.videoEl);
              if (avg) {
                const { r, g, b } = avg;
                setBgTint(
                  `linear-gradient(180deg, rgba(${r},${g},${b},0.24), rgba(0,0,0,0.46))`
                );
              }
            }
          });
        }
      },
      {
        root: null,
        rootMargin: "20% 0% 10% 0%",
        threshold: [0.05, 0.25, 0.5, 0.75],
      }
    );

    itemsEls.forEach((el) => io.observe(el));

    return () => {
      io.disconnect();
      cancelAnimationFrame(rafPickRef.current);
      rafPickRef.current = 0;
      ratioMapRef.current.clear();
    };
  }, [gridList.length, location.pathname, bgA, bgB, useAOnTop, visibleCount]);

  /* ===== highlighted video selection (prefer landscape) ===== */
  const highlightedVideo = useMemo(() => {
    if (!videosOrdered.length) return null;
    const landscape = videosOrdered.filter((v) => isPortraitHint(v) === false);
    const pool = landscape.length ? landscape : videosOrdered;
    return pool[0] || null;
  }, [videosOrdered]);

  const [hvLoaded, setHvLoaded] = useState(false);

  /* ===== videos lightbox ===== */
  const [videoLbOpen, setVideoLbOpen] = useState(false);
  const [videoLbIdx, setVideoLbIdx] = useState(0);

  const openVideoStripLightbox = (arg) => {
    let idx = 0;
    if (typeof arg === "number") {
      idx = arg;
    } else if (arg && typeof arg === "object") {
      const key = arg._id || arg.id || arg.url;
      const found = videosOrdered.findIndex(
        (v) => (v._id || v.id || v.url) === key
      );
      idx = found >= 0 ? found : 0;
    }
    setVideoLbIdx(Math.max(0, idx));
    setVideoLbOpen(true);
    document.documentElement.classList.add("no-scroll");
  };

  const closeVideoStripLightbox = () => {
    setVideoLbOpen(false);
    document.documentElement.classList.remove("no-scroll");
  };
  const nextVideoLb = () =>
    setVideoLbIdx((i) => (i + 1) % Math.max(1, videosOrdered.length));
  const prevVideoLb = () =>
    setVideoLbIdx(
      (i) =>
        (i - 1 + Math.max(1, videosOrdered.length)) %
        Math.max(1, videosOrdered.length)
    );

  useEffect(() => {
    if (!videoLbOpen) return;
    const onKey = (e) => {
      if (e.key === "Escape") closeVideoStripLightbox();
      else if (e.key === "ArrowRight") nextVideoLb();
      else if (e.key === "ArrowLeft") prevVideoLb();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [videoLbOpen, videosOrdered.length]);

  /* ----- grid lightbox (images/phrases) ----- */
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [lightIdx, setLightIdx] = useState(0);
  const [lbTint, setLbTint] = useState("rgba(0,0,0,0.85)");

  const openLightbox = (idx) => {
    setLightIdx(idx);
    setLightboxOpen(true);
    document.documentElement.classList.add("no-scroll");
  };
  const closeLightbox = () => {
    setLightboxOpen(false);
    document.documentElement.classList.remove("no-scroll");
  };
  const nextLight = () =>
    setLightIdx((i) => (i + 1) % Math.max(1, gridList.length));
  const prevLight = () =>
    setLightIdx(
      (i) =>
        (i - 1 + Math.max(1, gridList.length)) %
        Math.max(1, gridList.length)
    );

  useEffect(() => {
    if (!lightboxOpen) return;
    const it = gridList?.[lightIdx];
    if (!it) return;
    const url = it?.url || null;
    if (!url) return;
    const kind =
      String(it?.type || "").toLowerCase() === "video" ||
      /\.(mp4|mov|webm|avi)(\?|$)/i.test(url)
        ? "video"
        : "image";

    (async () => {
      try {
        if (kind === "image") {
          const avg = await averageFromImageSrc(url);
          if (avg) {
            const { r, g, b } = avg;
            setLbTint(`rgba(${r},${g},${b},0.38)`);
          } else {
            setLbTint("rgba(0,0,0,0.85)");
          }
        } else {
          setLbTint("rgba(0,0,0,0.72)");
        }
      } catch {
        setLbTint("rgba(0,0,0,0.85)");
      }
    })();
  }, [lightboxOpen, lightIdx, gridList]);

  /* ---------- render ---------- */
  if (loading || error) {
    return (
      <div className={`madagascar-gallery is-unframed`}>
        <div className="madagascar-hero-bg" aria-hidden="true" />
        <section className="entry-hero entry-hero--rect">
          <div className="entry-photo-wrap entry-photo-wrap--rect">
            <div className="entry-blackout entry-blackout--rect" />
            <div className="entry-veil entry-veil--rect" />
            <div className="entry-center">
              <div className="entry-title-ribbon">
                {titleContinent === "Antarctica" ? null : (
                  <h2 className="entry-continent">{titleContinent}</h2>
                )}
                <h1 className="entry-country">{titleCountry}</h1>
                {continentTeaser ? (
                  <p className="entry-teaser">{continentTeaser}</p>
                ) : null}
              </div>
            </div>
            {showScrollCue && (
              <button
                type="button"
                className="scroll-cue"
                aria-label="Scroll down"
                onClick={() =>
                  window.scrollTo({
                    top: window.innerHeight * 0.9,
                    behavior: "smooth",
                  })
                }
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
                  <path d="M12 16l-6-6h12l-6 6z" fill="currentColor" />
                </svg>
              </button>
            )}
          </div>
        </section>
      </div>
    );
  }

  return (
    <div
      className={`madagascar-gallery is-unframed`}
      style={{
        "--hero-img-opacity": heroImgOpacity,
        "--hero-tint-opacity": heroTintOpacity,
      }}
    >
      {/* Cinematic blurred background */}
      <div
        className="madagascar-hero-bg"
        aria-hidden="true"
        style={{ opacity: bgReady ? 1 : 0, transition: "opacity .3s ease" }}
      >
        {[{ src: bgA, key: "A", onTop: useAOnTop }, { src: bgB, key: "B", onTop: !useAOnTop }]
          .filter((b) => b.src)
          .map(({ src, key, onTop }) => {
            const isVideo = /\.(mp4|mov|webm|avi)(\?|$)/i.test(src);
            const className = `bg-layer ${onTop ? "top" : "bottom"}`;
            return !isVideo ? (
              <img
                key={`${key}:${src}`}
                className={className}
                src={src}
                alt=""
                decoding="async"
                onLoad={() => setBgReady(true)}
              />
            ) : (
              <video
                key={`${key}:${src}`}
                className={`${className} bg-video`}
                src={src}
                muted
                loop
                playsInline
                autoPlay
                preload="metadata"
                onLoadedData={() => setBgReady(true)}
              />
            );
          })}
        <div className="bg-tint" style={{ backgroundImage: bgTint }} />
      </div>

      {/* back home */}
      <div className="backhome-wrap">
        <Link to="/" className="back-home-link">
          ← Back home
        </Link>
      </div>

      {/* ===== ENTRY CARD (rectangular, full width, no rounding) ===== */}
      <section className="entry-hero entry-hero--rect">
        <div className="entry-photo-wrap entry-photo-wrap--rect">
          {entryMedia?.kind === "image" ? (
            <img
              key={`hero:${entryMedia.src}`}
              className="entry-photo entry-photo--rect"
              src={entryMedia.src}
              alt=""
              onLoad={() => setEntryLoaded(true)}
              decoding="async"
              loading="eager"
              style={{ opacity: entryLoaded ? 1 : 0 }}
            />
          ) : null}

          <div
            className={`entry-blackout entry-blackout--rect ${
              entryLoaded ? "is-gone" : ""
            }`}
            aria-hidden="true"
          />
          <div className="entry-veil entry-veil--rect" aria-hidden="true" />

          <div className="entry-center">
            <div className="entry-title-ribbon">
              {titleContinent === "Antarctica" ? null : (
                <h2 className="entry-continent">{titleContinent}</h2>
              )}
              <h1 className="entry-country">{titleCountry}</h1>
              {continentTeaser ? (
                <p className="entry-teaser">{continentTeaser}</p>
              ) : null}
            </div>
          </div>

          {showScrollCue && (
            <button
              type="button"
              className="scroll-cue"
              aria-label="Scroll down"
              onClick={() =>
                window.scrollTo({
                  top: window.innerHeight * 0.9,
                  behavior: "smooth",
                })
              }
            >
              <svg width="22" height="22" viewBox="0 0 24 24" aria-hidden="true">
                {/* filled upside-down triangle */}
                <path d="M12 16L6 10h12L12 16z" fill="currentColor" />
              </svg>
            </button>
          )}
        </div>
      </section>

      {/* ===== HIGHLIGHTED LANDSCAPE VIDEO (100vw, no rounding) ===== */}
      {highlightedVideo && (
        <section
          className="highlight-video-wrap highlight-video-wrap--full"
          aria-label="Featured video"
        >
          <div
            className="highlight-video highlight-video--full"
            onClick={() => openVideoStripLightbox(highlightedVideo)}
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" && openVideoStripLightbox(highlightedVideo)
            }
          >
            <video
              src={highlightedVideo.url}
              poster={highlightedVideo.poster || undefined}
              muted
              loop
              playsInline
              autoPlay
              preload="metadata"
              onLoadedData={() => setHvLoaded(true)}
            />
            <div className="motion-label">In Motion</div>
            <div className="expand-cue" aria-hidden="true" title="Expand">
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M9 3H3v6M3 3l7 7M15 21h6v-6M21 21l-7-7"
                  stroke="currentColor"
                  strokeWidth="1.8"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
              <span>Expand</span>
            </div>
          </div>
        </section>
      )}

      {/* ===== Stillness ===== */}

      {/* GRID (images + phrases only, ordered by `order`) */}
      <div ref={gridRef} className="media-grid">
        {showing.map((it, idx) => {
          const type = kindOfItem(it);
          if (type === "video") return null;

          const key =
            it?._id ??
            it?.id ??
            `${type}-${it?.url || it?.__phraseText || ""}-${idx}`;
          const id = String(idx);
          const half = !!it?.half;
          const framedOk = false;

          const ori = orientationOf(it);
          const phAR =
            ori === "portrait" || ori === "vertical" || ori === "tall"
              ? "2 / 3"
              : type === "video"
              ? "16 / 9"
              : "3 / 2";

          if (type === "phrase") {
            return (
              <PhraseTile
                key={key}
                id={id}
                index={idx}
                text={it.__phraseText || it.text || ""}
                onOpenLightbox={() => {}}
              />
            );
          }

          return (
            <ImageTile
              key={key}
              id={id}
              index={idx}
              url={it?.url}
              half={half}
              isActive={false}
              onBecameActive={() => {}}
              framedOk={framedOk}
              phAR={phAR}
              onOpenLightbox={() => {}}
            />
          );
        })}
      </div>

      {/* LOAD MORE */}
      {remaining > 0 && (
        <div className="load-more-wrap">
          <button
            type="button"
            className="load-more"
            onClick={handleLoadMoreClick}
          >
            Keep exploring ({remaining})
          </button>
          <div ref={sentinelRef} className="load-sentinel" />
        </div>
      )}

      {/* back to top */}
      <div className="back-to-top-wrap">
        <button
          type="button"
          className="back-to-top"
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
        >
          ↑ Back to top
        </button>
      </div>

      {/* ===== PORTALLED LIGHTBOXES (topmost) ===== */}
      {lightboxOpen &&
        createPortal(
          <div
            className="lightbox lightbox--topmost"
            role="dialog"
            aria-modal="true"
            style={{ background: lbTint }}
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              setLightIdx(
                x > rect.width / 2
                  ? (lightIdx + 1) % Math.max(1, gridList.length)
                  : (lightIdx - 1 + Math.max(1, gridList.length)) %
                      Math.max(1, gridList.length)
              );
            }}
          >
            <button
              className="lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setLightboxOpen(false);
                document.documentElement.classList.remove("no-scroll");
              }}
              aria-label="Close"
            >
              ×
            </button>
            <button
              className="lightbox-nav left"
              onClick={(e) => {
                e.stopPropagation();
                setLightIdx(
                  (i) =>
                    (i - 1 + Math.max(1, gridList.length)) %
                    Math.max(1, gridList.length)
                );
              }}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className="lightbox-nav right"
              onClick={(e) => {
                e.stopPropagation();
                setLightIdx(
                  (i) => (i + 1) % Math.max(1, gridList.length)
                );
              }}
              aria-label="Next"
            >
              ›
            </button>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              {(() => {
                const it = gridList[lightIdx] || null;
                if (!it) return null;
                const t = kindOfItem(it);
                if (t === "video") {
                  return (
                    <video
                      className="lightbox-media"
                      key={`lb:${lightIdx}`}
                      src={it.url}
                      poster={it.poster || undefined}
                      autoPlay
                      playsInline
                      muted
                      loop
                      onLoadedData={(e) => e.currentTarget.play().catch(() => {})}
                    />
                  );
                } else if (t === "phrase") {
                  return (
                    <blockquote className="lightbox-phrase">
                      {it.__phraseText || it.text || ""}
                    </blockquote>
                  );
                }
                return <img className="lightbox-media" src={it.url} alt="" />;
              })()}
            </div>
          </div>,
          document.body
        )}

      {videoLbOpen &&
        createPortal(
          <div
            className="lightbox lightbox--topmost"
            role="dialog"
            aria-modal="true"
            onClick={(e) => {
              const rect = e.currentTarget.getBoundingClientRect();
              const x = e.clientX - rect.left;
              setVideoLbIdx((i) =>
                x > rect.width / 2
                  ? (i + 1) % Math.max(1, videosOrdered.length)
                  : (i - 1 + Math.max(1, videosOrdered.length)) %
                      Math.max(1, videosOrdered.length)
              );
            }}
          >
            <button
              className="lightbox-close"
              onClick={(e) => {
                e.stopPropagation();
                setVideoLbOpen(false);
                document.documentElement.classList.remove("no-scroll");
              }}
              aria-label="Close"
            >
              ×
            </button>
            <button
              className="lightbox-nav left"
              onClick={(e) => {
                e.stopPropagation();
                setVideoLbIdx(
                  (i) =>
                    (i - 1 + Math.max(1, videosOrdered.length)) %
                    Math.max(1, videosOrdered.length)
                );
              }}
              aria-label="Previous"
            >
              ‹
            </button>
            <button
              className="lightbox-nav right"
              onClick={(e) => {
                e.stopPropagation();
                setVideoLbIdx(
                  (i) => (i + 1) % Math.max(1, videosOrdered.length)
                );
              }}
              aria-label="Next"
            >
              ›
            </button>
            <div className="lightbox-content" onClick={(e) => e.stopPropagation()}>
              {(() => {
                const it = videosOrdered[videoLbIdx] || null;
                if (!it) return null;
                return (
                  <video
                    className="lightbox-media"
                    key={`vstrip:${videoLbIdx}`}
                    src={it.url}
                    poster={it.poster || undefined}
                    autoPlay
                    playsInline
                    muted
                    loop
                    onLoadedData={(e) => e.currentTarget.play().catch(() => {})}
                  />
                );
              })()}
            </div>
          </div>,
          document.body
        )}

      {/* ===== Dynamic AudioFloat (NEW) ===== */}
      <AudioFloat
        srcs={[
          { src: musicSrc, type: "audio/mpeg" },
        ]}
        startMuted={false}
        startVolume={0.5}
        loop={true}
        label="Site background audio"
        startTime={musicStart}
      />
    </div>
  );
}
